import { useEffect, useMemo, useState } from "react";
import { PaginationBar } from "@/components/common/PaginationBar";
import { usePagination } from "@/components/common/usePagination";
import {
  ghostBtnClass,
  mfgInlineInputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { NamedVariantConfirmStatus } from "@/lib/manufacturing/namedVariantsClient";
import type { MfgRecipeKind, MfgRecipeSummary } from "@/lib/manufacturing/types";
import { ManufacturingNamedVariantApplyWorkingBanner } from "./ManufacturingNamedVariantApplyWorkingBanner";
import { ManufacturingNamedVariantDictateAppend } from "./ManufacturingNamedVariantDictateAppend";

const KIND_FILTERS: Array<{ id: MfgRecipeKind | "all"; label: string }> = [
  { id: "all", label: "All" },
  { id: "assembly", label: "Assembly" },
  { id: "product", label: "Product" },
  { id: "package", label: "Package" },
];

type AssignmentFilter = "all" | "unassigned" | "assigned";

const EMPTY_ASSIGNED: ReadonlyMap<string, NamedVariantConfirmStatus> = new Map();

const ASSIGNMENT_FILTERS: Array<{ id: AssignmentFilter; label: string }> = [
  { id: "all", label: "All recipes" },
  { id: "unassigned", label: "Not assigned" },
  { id: "assigned", label: "Already assigned" },
];

function assignmentTag(status: NamedVariantConfirmStatus): {
  label: string;
  className: string;
} {
  if (status === "pending") {
    return {
      label: "Assigned · pending",
      className:
        "rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900 dark:bg-amber-950/50 dark:text-amber-200",
    };
  }
  if (status === "rejected") {
    return {
      label: "Assigned · rejected",
      className:
        "rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rose-900 dark:bg-rose-950/40 dark:text-rose-200",
    };
  }
  return {
    label: "Assigned",
    className:
      "rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200",
  };
}

export function ManufacturingNamedVariantApplyRecipesPicker({
  recipes,
  selectedIds,
  onSelectedIdsChange,
  description,
  onDescriptionChange,
  saveBusy,
  applyBusy = false,
  applyRecipeCount = 0,
  onApply,
  assignedByParentId,
}: {
  recipes: MfgRecipeSummary[];
  selectedIds: string[];
  onSelectedIdsChange: (ids: string[]) => void;
  description: string;
  onDescriptionChange: (next: string) => void;
  saveBusy: boolean;
  applyBusy?: boolean;
  applyRecipeCount?: number;
  onApply: () => void;
  /** parent_recipe_id → confirm_status for applications of the current variant */
  assignedByParentId?: ReadonlyMap<string, NamedVariantConfirmStatus>;
}) {
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState<MfgRecipeKind | "all">("all");
  const [assignmentFilter, setAssignmentFilter] = useState<AssignmentFilter>("all");

  const assigned = assignedByParentId ?? EMPTY_ASSIGNED;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return recipes.filter((r) => {
      if (!r.is_active) return false;
      if (r.kind === "variant" || r.kind === "option") return false;
      if (kindFilter !== "all" && r.kind !== kindFilter) return false;
      const isAssigned = assigned.has(r.id);
      if (assignmentFilter === "assigned" && !isAssigned) return false;
      if (assignmentFilter === "unassigned" && isAssigned) return false;
      if (!q) return true;
      return `${r.code} ${r.name}`.toLowerCase().includes(q);
    });
  }, [recipes, search, kindFilter, assignmentFilter, assigned]);

  const pager = usePagination(filtered, 10);

  useEffect(() => {
    pager.setPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset page on filter change only
  }, [search, kindFilter, assignmentFilter]);

  function toggle(id: string) {
    onSelectedIdsChange(
      selectedIds.includes(id)
        ? selectedIds.filter((x) => x !== id)
        : [...selectedIds, id],
    );
  }

  function selectPage() {
    const pageIds = pager.paginatedItems.map((r) => r.id);
    onSelectedIdsChange([...new Set([...selectedIds, ...pageIds])]);
  }

  function clearSelection() {
    onSelectedIdsChange([]);
  }

  return (
    <div className="space-y-2">
      {applyBusy ? (
        <ManufacturingNamedVariantApplyWorkingBanner
          recipeCount={applyRecipeCount || selectedIds.length}
        />
      ) : null}

      <div className="space-y-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs font-semibold text-gray-700 dark:text-zinc-300">
            AI instructions
          </span>
          <ManufacturingNamedVariantDictateAppend
            disabled={saveBusy || applyBusy}
            onAppend={(chunk) => {
              const base = description.trimEnd();
              onDescriptionChange(base ? `${base} ${chunk}` : chunk);
            }}
          />
        </div>
        <textarea
          className={`${mfgInlineInputClass} min-h-[64px]`}
          placeholder="Describe the upgrade (e.g. convert joists and band boards to the next size up at the same length)"
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          disabled={applyBusy}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          className={`${mfgInlineInputClass} min-w-[160px] flex-1`}
          placeholder="Search recipes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className={mfgInlineInputClass}
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value as MfgRecipeKind | "all")}
        >
          {KIND_FILTERS.map((k) => (
            <option key={k.id} value={k.id}>
              {k.label}
            </option>
          ))}
        </select>
        <select
          className={mfgInlineInputClass}
          value={assignmentFilter}
          onChange={(e) => setAssignmentFilter(e.target.value as AssignmentFilter)}
          title="Filter by whether this variant is already applied"
        >
          {ASSIGNMENT_FILTERS.map((f) => (
            <option key={f.id} value={f.id}>
              {f.label}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-wrap gap-2 text-xs">
        <button type="button" className={ghostBtnClass} onClick={selectPage}>
          Select page
        </button>
        <button type="button" className={ghostBtnClass} onClick={clearSelection}>
          Clear selection
        </button>
        <span className="self-center text-gray-500 dark:text-zinc-400">
          {selectedIds.length} selected
        </span>
      </div>

      <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-gray-200 p-2 dark:border-zinc-700">
        {pager.paginatedItems.map((r) => {
          const checked = selectedIds.includes(r.id);
          const status = assigned.get(r.id);
          const tag = status ? assignmentTag(status) : null;
          return (
            <label key={r.id} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={checked} onChange={() => toggle(r.id)} />
              <span className="min-w-0 flex-1">
                {r.code} — {r.name}{" "}
                <span className="text-xs text-gray-400">({r.kind})</span>
              </span>
              {tag ? <span className={tag.className}>{tag.label}</span> : null}
            </label>
          );
        })}
        {pager.paginatedItems.length === 0 ? (
          <div className="py-4 text-center text-sm text-gray-500">No matching recipes.</div>
        ) : null}
      </div>

      <PaginationBar
        pageIndex={pager.pageIndex}
        pageCount={pager.pageCount}
        pageSize={pager.pageSize}
        totalCount={pager.totalCount}
        firstShown={pager.firstShown}
        lastShown={pager.lastShown}
        onPageChange={pager.setPageIndex}
        onPageSizeChange={pager.setPageSize}
        noun="recipes"
        alwaysShow
      />

      <div className="flex justify-end">
        <button
          type="button"
          className={mfgPrimaryBtnClass}
          disabled={saveBusy || applyBusy || selectedIds.length === 0}
          onClick={onApply}
        >
          {applyBusy
            ? `Applying to ${applyRecipeCount || selectedIds.length}…`
            : `Apply to ${selectedIds.length || 0} recipes`}
        </button>
      </div>
    </div>
  );
}
